import React, { useEffect } from 'react';
import useDispatch from '@hooks/useDispatch';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Spinner,
} from '@chakra-ui/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import config from '@config';
import { getSingleParam, useParam } from '@hooks/useParam';
import { clearMessages } from '@slices/terminalSlices';
import ChatbotDetails from '@components/ChatbotDetails';
import Navigation from '@components/Navigation';
import RasaChatbot from '@components/RasaChatbot';
import InfobipWidget, { removeInfobipElemets } from '@components/InfobipWidget';
import { ProjectInfoDto, ProjectType } from '@projectTypes/Project';
import { Meta } from '@components/Meta';
import { safeParseJSON } from '@helpers/safeParseJSON';
import projectService from '@services/ProjectService';
import { CustomPage } from '@projectTypes/index';
import useIsEn from '@hooks/useIsEn';
import { useTranslation } from 'next-i18next';

type Props = {
  projectInfo: ProjectInfoDto;
};

const Chat: CustomPage<Props> = (props) => {
  const { projectInfo } = props;

  const dispatch = useDispatch();
  const projectId = useParam('projectId');

  const isAnswers = projectInfo?.type === ProjectType.ANSWERS;

  const isEn = useIsEn();
  const { t } = useTranslation('common');

  useEffect(() => {
    if (projectId) {
      dispatch(clearMessages());
      // TODO: remove this quickfix
      window.scrollTo(0, 0);
    }
  }, [projectId]);

  useEffect(() => {
    if (!isAnswers) {
      const interval = setInterval(() => {
        removeInfobipElemets();
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
    return () => {
      removeInfobipElemets();
    };
  }, [projectId, isAnswers]);

  const chatUrl = typeof window === 'undefined' ? '' : window.location.href;
  const info = safeParseJSON(projectInfo.info);
  const infoEn = safeParseJSON(projectInfo.infoEn);

  const shortDescription =
    (isEn ? (infoEn ? infoEn[config.shortDescription] : null) : null) ||
    info[config.shortDescription];

  return (
    <Box
      background="white"
      maxH="100vh"
      minH="100vh"
      display="flex"
      flexDirection="column"
    >
      <Navigation />
      {false && projectInfo?.type === ProjectType.ANSWERS && (
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>{t('chatbot.outOfService')}</AlertTitle>
          <AlertDescription>{t('chatbot.thankYou')}</AlertDescription>
        </Alert>
      )}
      {projectInfo && (
        <Meta
          title={`${projectInfo.name} | AI Chatbot | STEMI`}
          description={shortDescription}
          imageUrl={`${config.host}/images/showcase-chatbot.png`}
        />
      )}

      <Box
        display={['block', 'block', 'flex']}
        h="100%"
        background="white"
        flex={1}
        flexDirection={['column', 'column', 'row']}
        overflow={['unset', 'unset', 'scroll']}
      >
        <>
          {projectInfo ? (
            <>
              <ChatbotDetails {...projectInfo} chatUrl={chatUrl} />
              {projectInfo?.type === ProjectType.ANSWERS ? (
                <InfobipWidget
                  id={
                    projectInfo.attributes?.answers_id ||
                    '30f0b89c-5f33-408d-be55-d19ba79567e6'
                  }
                />
              ) : (
                <RasaChatbot projectId={projectId} />
              )}
            </>
          ) : (
            <Spinner />
          )}
        </>
      </Box>
    </Box>
  );
};

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const translations = await serverSideTranslations(ctx.locale, ['common']);
  const { projectId } = ctx.params;
  const projectInfo = await projectService.getProjectInfo(
    getSingleParam(projectId),
  );

  return {
    props: {
      ...translations,
      projectInfo,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const projects = await projectService.getShowcaseProjects();
  return {
    paths: projects.map((p) => ({ params: { projectId: p.id } })),
    fallback: 'blocking',
  };
};

export default Chat;
